import type { Home, IncidentReport } from '@lmc/ts-common'
import useBodyMaps from 'hooks/useBodyMaps'
import useResident from 'hooks/useResident'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { step5Answers } from '../steps/injuriesAndDamages/appInjuries'
import { useServiceUserWords } from '../useServiceUserWords'
import {
  type InjuriesCase,
  injuryBodyMapIds,
  injuryEntries
} from '../view/readRules'
import { useServiceUserName, useTeamMemberName } from '../view/usePersonName'
import { buildIncidentCaseV2Document } from './incidentCaseV2Document'

/**
 * Looks up everything a v2 case's document names (team members, service
 * users, the service user's preferred name and linked body maps) and builds
 * it once they have loaded. The same queries as the case view, so on the
 * case page they are already cached.
 */
const useIncidentCaseV2Document = (incidentReport: IncidentReport) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const serviceUserWords = useServiceUserWords()
  const serviceUsers = useServiceUserName()
  const teamMembers = useTeamMemberName()
  const resident = useResident({ id: incidentReport.serviceUser.id })
  const savedCase = (incidentReport.case ?? {}) as InjuriesCase
  const hasBodyMaps = injuryEntries(step5Answers(savedCase).injuries).some(
    ({ entry }) => injuryBodyMapIds(entry, savedCase).length > 0
  )
  // Only fetched when an injury links one, as the case view does.
  const bodyMaps = useBodyMaps({
    residentId: hasBodyMaps ? incidentReport.serviceUser.id : undefined
  })

  const buildDocument = () =>
    buildIncidentCaseV2Document(incidentReport, {
      bodyMaps: bodyMaps.bodyMaps,
      home,
      preferredName: resident.resident?.preferredName,
      serviceUserName: serviceUsers.nameOf,
      serviceUserWords,
      t,
      teamMemberName: teamMembers.nameOf
    })

  return {
    buildDocument,
    // Not the resident's: without it the name prints without the preferred
    // name, as the view shows it.
    isError: bodyMaps.isError || serviceUsers.isError || teamMembers.isError,
    isLoading:
      bodyMaps.isLoading ||
      resident.isLoading ||
      serviceUsers.isLoading ||
      teamMembers.isLoading
  }
}

export default useIncidentCaseV2Document
